import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { CooperationActionsService } from '@app/shared/services/actions/cooperation-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { CooperationsDataService } from '@app/shared/services/cooperations-data.service';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-cooperation-projects-list',
  standalone: true,
  imports: [TranslatePipe, UiButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './cooperation-projects-list.component.html'
})
export class CooperationProjectsListComponent {
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly cooperationsDataService = inject(CooperationsDataService);
  private readonly cooperationsActionsService = inject(CooperationActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly projectsDataService = inject(ProjectsDataService);
  private readonly translateService = inject(TranslateService);

  protected cooperationProjects = computed(() =>
    this.cooperationId() ? this.cooperationsDataService.getCooperationProjects(this.cooperationId()!)() : undefined
  );

  protected companyId = this.activeCompanyService.activeCompanyId;

  protected companyProjects = computed(() => {
    const cid = this.companyId();
    if (!cid) return [];
    return this.projectsDataService.getCompanyProjects(cid)() ?? [];
  });

  protected canEditProjects = computed(() => {
    const id = this.cooperationId();
    if (!id) return false;
    const c = this.cooperationsDataService.getById(id)();
    const status = c?.status;
    return status === CooperationStatusEnum.Pending || status === CooperationStatusEnum.Accepted;
  });

  protected currentProjectIds = computed(() => {
    const list = this.cooperationProjects() ?? [];
    return list.map(cp => cp.project()?.id).filter((id): id is string => !!id);
  });

  protected availableProjectsToAdd = computed(() => {
    const current = this.currentProjectIds();
    const all = this.companyProjects();
    return all.filter(p => !current.includes(p.id));
  });

  public readonly cooperationId = input<string | undefined>(undefined);
  public readonly writable = input<boolean>(false);

  public constructor() {
    effect(() => {
      const id = this.cooperationId();
      if (id) this.cooperationsDataService.loadCooperationProjects(id);
    });

    effect(() => {
      if (!this.writable()) return;
      const cid = this.companyId();
      if (cid) this.projectsDataService.loadCompanyProjects(cid);
    });
  }

  protected openAddProjectsDialog(): void {
    const coopId = this.cooperationId();
    if (!coopId || !this.canEditProjects()) return;

    const ref = this.dialogService.openSelectCooperationProjectsDialog({
      projects: this.availableProjectsToAdd()
    });
    ref.afterClosed().subscribe(selectedIds => {
      if (selectedIds?.length) {
        const nextIds = [...this.currentProjectIds(), ...selectedIds];
        this.cooperationsActionsService.setCooperationProjects(coopId, nextIds).subscribe();
      }
    });
  }

  protected removeProject(projectId: string): void {
    const coopId = this.cooperationId();
    if (!coopId || !projectId || !this.canEditProjects()) return;

    const ref = this.dialogService.openConfirmationDialog({
      title: this.translateService.instant('cooperations.remove-project-confirmation-title'),
      desc: this.translateService.instant('cooperations.remove-project-confirmation-desc')
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const nextIds = this.currentProjectIds().filter(id => id !== projectId);
        this.cooperationsActionsService.setCooperationProjects(coopId, nextIds).subscribe();
      }
    });
  }
}
